import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../directives/TranslatePipe';
import { ModifiedFieldBadgeComponent } from '../modified-field-badge/modified-field-badge.component';
import { FieldContextDirective } from '../../directives/FieldContext.directive';
import { FIELD_HOST, IFieldHost } from '../../../_models/IFieldHost';
import { TimeZoneIconComponent } from '../timezone-icon/timezone-icon.component';
import { GenericComponent } from '../../_base/GenericComponent';
import { SiteTimeZoneService } from '../../../services/SiteTimeZoneService';
import { TranslationService } from '../../../services/TranslationService';


@Component({
  selector: 'app-field-wrapper',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ModifiedFieldBadgeComponent, TimeZoneIconComponent],
  templateUrl: './field-wrapper.component.html'
})
export class FieldWrapperComponent {
  @Input() property!: string;
  @Input() businessProperty?: string; // propriété métier si elle est différente de la propriété vue(pour les changements)
  @Input() labelPosition: 'above' | 'below' = 'above';
  /** Optionnel : clé de traduction explicite. Par défaut, dérivée des métadonnées de l'entité (PropretyKey). */
  @Input() label?: string;

  // ---------------------------------------------------------------------------
  // Mode legacy (compat) : si [entity] est bindé, ces inputs font foi.
  // Nouveau mode : ne binder que property (+ label/businessProperty au besoin),
  // le contexte vient d'un [fieldContext] ancêtre ou du token FIELD_HOST.
  // ---------------------------------------------------------------------------
  @Input() entity: any;
  @Input() isEditMode = false;
  @Input() flashingProperty: string | null = null;
  @Input() index!: number;
  @Input() totalModified!: number;
  @Input() toggleUndo!: (property: string) => void;
  @Input() triggerFlash!: (property: string) => void;
  @Input() hasValidationErrors!: (property: string) => boolean;
  @Input() getValidationErrors!: (property: string) => string[];
  @Input() propretyHasChanged!: (property: string) => boolean;
  @Input() skin?: any;                                                // pour les classes de style

  @ViewChild('wrapper', { static: false }) wrapper?: ElementRef;
  isCheckbox = false;
  uniqueId = `field-${Math.random().toString(36).substr(2, 9)}`;

  /** Contexte ambiant : directive [fieldContext] ancêtre, sinon token FIELD_HOST. */
  private readonly ctx = inject(FieldContextDirective, { optional: true });
  private readonly tokenHost = inject(FIELD_HOST, { optional: true });
  private readonly siteTimeZoneService = inject(SiteTimeZoneService);
  private readonly translationService = inject(TranslationService);


  constructor(private cdr: ChangeDetectorRef) {
    // Flash auto (mode contexte uniquement) : applique bg-yellow-200 sur le contrôle
    // projeté quand l'hôte signale un undo/redo sur cette propriété.
    // Les écrans legacy gardent leur binding [class.bg-yellow-200] manuel.
    effect(() => {
      if (this.isLegacy) return;
      const host = this.ctx?.host ?? this.tokenHost;
      if (!host) return;
      const flashing = host.flashingProperty() === this.trackedProperty;
      const el = this.wrapper?.nativeElement?.querySelector('input, select, textarea');
      el?.classList.toggle('bg-yellow-200', flashing);
    });
  }

  // ---------- Résolution du contexte : inputs legacy > directive > token > erreur ----------

  /** Les écrans legacy bindent toujours [entity] : c'est le discriminant du mode. */
  private get isLegacy(): boolean {
    return this.entity !== undefined;
  }

  private get host(): IFieldHost {
    const h = this.ctx?.host ?? this.tokenHost;
    if (!h) {
      throw new Error(
        `app-field-wrapper('${this.property}') : aucun IFieldHost trouvé. ` +
        `Posez [fieldContext]="this" sur la racine du formulaire (ou providers: [provideFieldHost(...)]) ` +
        `ou bindez les inputs explicites.`);
    }
    return h;
  }

  get trackedProperty(): string {
    return this.businessProperty || this.property;
  }

  get effEntity(): any {
    return this.isLegacy ? this.entity : this.host.editingEntity();
  }

  get effIsEditMode(): boolean {
    return this.isLegacy ? this.isEditMode : this.host.isEditMode();
  }

  get effSkin(): any {
    return this.skin ?? (this.isLegacy ? undefined : this.host.Skin);
  }

  /** label explicite > PropretyKey des métadonnées > convention entities.{entité}.propreties.{prop} > property */
  get effLabel(): string {
    if (this.label) return this.label;
    const meta = this.effEntity?.GetType?.();
    const fromMeta = meta?.PropretyDescriptors?.[this.property]?.PropretyKey;
    if (fromMeta) return fromMeta;
    if (meta?.EntityName) {
      const entityKey = meta.EntityName.charAt(0).toLowerCase() + meta.EntityName.slice(1);
      return `entities.${entityKey}.propreties.${this.property}`;
    }
    return this.property;
  }

  get effIndex(): number {
    return this.isLegacy ? this.index : this.host.getModifiedFieldIndex(this.trackedProperty);
  }

  get effTotalModified(): number {
    return this.isLegacy ? this.totalModified : this.host.totalModifiedCount();
  }

  effPropretyHasChanged(): boolean {
    return this.isLegacy
      ? this.propretyHasChanged(this.trackedProperty)
      : this.host.propretyHasChanged(this.trackedProperty);
  }

  effHasValidationErrors(): boolean {
    return this.isLegacy
      ? this.hasValidationErrors(this.property)
      : this.host.hasValidationErrors(this.property);
  }

  effGetValidationErrors(): string[] {
    return this.isLegacy
      ? this.getValidationErrors(this.property)
      : this.host.getValidationErrors(this.property);
  }

  /** Référence stable passée au badge (pas de .bind(this) recréé à chaque CD). */
  effToggleUndo = (property: string): void => {
    if (this.isLegacy) this.toggleUndo(property);
    else this.host.toggleUndo(property);
  };

  effTriggerFlash(property: string): void {
    if (this.isLegacy) this.triggerFlash(property);
    else this.host.triggerFlash(property);
  }

  getDisplayValue(): string {
    const entity = this.effEntity;
    if (!entity) return ' ';

    // supporte les propriétés imbriquées ('relatedTenant.city')
    const value = this.property.split('.').reduce((obj: any, key: string) => obj?.[key], entity);
    if (value == null) return '';

    // Un champ de date se rend comme dans les listes, pas en chaine brute.
    if (this.proprieteEstDate()) {
      const date = value instanceof Date ? value : new Date(value);
      if (!isNaN(date.getTime())) {
        return GenericComponent.GetFormatedDateTimeValue(
          date,
          this.translationService.instant('app.short-local-date-time'),
          this.translationService.instant('app.time-output-format'));
      }
    }

    return String(value);
  }

  /** Le champ porte-t-il une date ? Lu dans les métadonnées, comme le fait la liste. */
  private proprieteEstDate(): boolean {
    const meta = this.effEntity?.GetType?.();
    if (!meta) return false;
    return Reflect.getMetadata('design:type', meta, this.property) === Date;
  }

  /** Fuseau du site, pour un champ de date seulement. */
  get fuseauDuChamp(): string | null {
    if (!this.proprieteEstDate()) return null;
    return this.siteTimeZoneService.fuseauDuSite(this.effEntity?.fK_PointOfService);
  }

  ngAfterViewInit() {
    // Essayer plusieurs fois avec des délais différents
    const checkForCheckbox = (attempt = 0) => {
      const inputEl = this.wrapper?.nativeElement?.querySelector('input[type="checkbox"]');

      if (inputEl) {
        this.isCheckbox = true;
        if (!inputEl.id) {
          inputEl.id = this.uniqueId;
        }
        inputEl.classList.add('cursor-pointer');

        this.cdr.detectChanges();
      } else if (attempt < 5) {
        // Réessayer jusqu'à 5 fois avec des délais croissants
        setTimeout(() => checkForCheckbox(attempt + 1), 50 * (attempt + 1));
      }
    };

    setTimeout(() => checkForCheckbox(), 100);
  }

}
